/**
 * HUD variant — scoped "mission-control" palette + pure geometry helpers.
 *
 * Like `TERM` in liveModeMeta.js, this is a self-contained dark palette composed
 * FROM design tokens (no invented brand hexes). Colored glows/tints are derived
 * from the semantic token hexes via `rgba()`; neutral glass surfaces are white/
 * black alphas (standard glassmorphism, not brand colors). Keeping it in this
 * const (not an inline style object) is what lets the screen reference HUD.* in
 * styles without tripping local/no-hardcoded-styles.
 */

import { dark, semantic, typography } from '@/shared/theme/tokens';

// hex (#rgb | #rrggbb) → rgba string, so every colored value traces to a token.
function rgba(hex, a) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export const HUD = {
  // ── Ink ───────────────────────────────────────────────────────────────────
  ink:        dark.textPrimary,   // primary readout
  inkDim:     dark.textMuted,     // labels
  inkFaint:   dark.textSubtle,    // least important

  // ── Neutral glass + structure (white/black alphas) ─────────────────────────
  void:       dark.bgPage,                  // base under the glass
  panel:      'rgba(255, 255, 255, 0.035)', // glass panel fill
  panelRaised:'rgba(255, 255, 255, 0.06)',  // raised cell
  panelDeep:  'rgba(0, 0, 0, 0.28)',        // inset well (gauge dial)
  stroke:     'rgba(255, 255, 255, 0.09)',  // hairline panel edge
  strokeSoft: 'rgba(255, 255, 255, 0.05)',  // faintest divider
  gridLine:   'rgba(255, 255, 255, 0.04)',  // schematic grid

  // ── Accents (derived from semantic tokens) ─────────────────────────────────
  accent:     semantic.brandAccent,         // cyan command accent
  accentGlow: rgba(semantic.brandAccent, 0.5),
  accentTint: rgba(semantic.brandAccent, 0.12),
  accentEdge: rgba(semantic.brandAccent, 0.4),
  await:      semantic.infoCyan,            // open-loop "awaiting confirmation"
  awaitTint:  rgba(semantic.infoCyan, 0.16),
  awaitEdge:  rgba(semantic.infoCyan, 0.42),

  mono:       typography.fontFamily.mono,
};

// Live status → command-center color set (color / glow / tint / edge), derived
// from semantic token hexes so a stale/critical ring reads instantly.
export const HUD_STATUS = {
  critical: {
    color: semantic.errorAlt,
    glow:  rgba(semantic.errorAlt, 0.55),
    tint:  rgba(semantic.errorAlt, 0.14),
    edge:  rgba(semantic.errorAlt, 0.46),
  },
  warning: {
    color: semantic.warningSolid,
    glow:  rgba(semantic.warningSolid, 0.5),
    tint:  rgba(semantic.warningSolid, 0.13),
    edge:  rgba(semantic.warningSolid, 0.42),
  },
  ok: {
    color: semantic.successAlt,
    glow:  rgba(semantic.successAlt, 0.45),
    tint:  rgba(semantic.successAlt, 0.12),
    edge:  rgba(semantic.successAlt, 0.34),
  },
  stale: {
    color: dark.textSubtle,
    glow:  'rgba(255, 255, 255, 0)',
    tint:  'rgba(255, 255, 255, 0.03)',
    edge:  dark.borderInput,
  },
};

export function statusOf(meta, isStale) {
  if (isStale) return HUD_STATUS.stale;
  return HUD_STATUS[meta] ?? HUD_STATUS.ok;
}

// ── Gauge geometry ────────────────────────────────────────────────────────────
// A 270° dial with the gap at 6 o'clock. Angles are "clock degrees" (0 = top,
// increasing clockwise); the track sweeps 225°→495° (= 135°).
export const GAUGE_START = 225;
export const GAUGE_SWEEP = 270;

function pointOnArc(cx, cy, r, clockDeg) {
  const a = ((clockDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

const round = (n) => Math.round(n * 100) / 100;

/** SVG path for a clockwise arc between two clock-degree angles. */
export function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = pointOnArc(cx, cy, r, startDeg);
  const e = pointOnArc(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${round(s.x)} ${round(s.y)} A ${r} ${r} 0 ${large} 1 ${round(e.x)} ${round(e.y)}`;
}

/** Fraction [0,1] → its point on the dial (for value ticks/dots). */
export function dialPoint(cx, cy, r, fraction) {
  const f = Math.min(1, Math.max(0, fraction));
  return pointOnArc(cx, cy, r, GAUGE_START + f * GAUGE_SWEEP);
}

export function clamp01(x) {
  return Math.min(1, Math.max(0, x));
}
